import { sortBy } from 'lodash';
import { useMemo } from 'react';

import { AISuggestionsMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { isDPMatcherVariant } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.utils';
import { useAiSuggestionsMatcherContext } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';
import { useRecipientData } from '@/modules/dispositiveProvisions/contexts/RecipientData.context';
import {
  useRecipientOptionsFromHousehold,
  useRecipientOptionsParams,
} from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useRecipientOptions';
import { useFetchBusinessEntityDetailsOptions } from '@/modules/entities/BusinessEntityDetailsSubform/useGetBusinessEntityOwnerOptions';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { SURVIVING_SPOUSE_SENTINEL } from '@/modules/entities/EntityBeneficiariesSubform/EntityBeneficiariesSubform.constants';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

interface UseAISuggestionsMatcherOptionsProps {
  matcherVariant: AISuggestionsMatcherVariant;
  suggestionKind: AiSuggestionKind;
  suggestionId: string;
}

/**
 * Returns the grouped options to match AI suggestions to existing clients/organizations/entities.
 *
 * @param matcherVariant
 * @param suggestionKind
 */
export function useAISuggestionsMatcherGroupedOptions({
  matcherVariant,
  suggestionKind,
  suggestionId,
}: UseAISuggestionsMatcherOptionsProps) {
  const {
    householdId,
    isTwoClientHousehold,
    possibleGrantors,
    possibleTrustees,
    possibleBeneficiaries,
  } = useHouseholdDetailsContext();

  const { entityId } = useEntityDetailsContext();

  const { matchedSuggestions } = useAiSuggestionsMatcherContext();

  // A match must remain selectable in the row that owns it, but disabled everywhere else.
  const matchesFromOtherRows = useMemo(
    () =>
      Object.entries(matchedSuggestions)
        .filter(([matchedSuggestionId]) => matchedSuggestionId !== suggestionId)
        .map(([, match]) => match),
    [matchedSuggestions, suggestionId]
  );

  const { matchedOwnerIds, matchedKeyPersonIds } = useMemo(() => {
    return {
      matchedOwnerIds: matchesFromOtherRows
        .filter((m) => m.suggestion.kind === AiSuggestionKind.BeneficialOwner)
        .map(
          (m) => m.clientProfileId || m.clientOrganizationId || m.entityId || ''
        )
        .filter(Boolean),
      matchedKeyPersonIds: matchesFromOtherRows
        .filter((m) => m.suggestion.kind === AiSuggestionKind.KeyPerson)
        .map(
          (m) => m.clientProfileId || m.clientOrganizationId || m.entityId || ''
        )
        .filter(Boolean),
    };
  }, [matchesFromOtherRows]);

  const { ownerOptions, keyPeopleOptions } =
    useFetchBusinessEntityDetailsOptions(
      householdId ?? '',
      entityId ?? null,
      matchedOwnerIds,
      matchedKeyPersonIds
    );

  // Client profiles/organizations that are already on luminary to match suggestions to.
  const existingOptions = useMemo(() => {
    switch (matcherVariant) {
      case AISuggestionsMatcherVariant.GRANTORS:
      case AISuggestionsMatcherVariant.SINGLE_GRANTOR:
        return {
          clients: (possibleGrantors ?? []).map((g) => ({
            id: g.id,
            name: g.displayName,
          })),
          // Grantors cannot be organizations.
          organizations: [],
        };
      case AISuggestionsMatcherVariant.TRUSTEES:
      case AISuggestionsMatcherVariant.SUBTRUST_TRUSTEES:
        return possibleTrustees ?? { clients: [], organizations: [] };
      case AISuggestionsMatcherVariant.BENEFICIARIES_V2:
      case AISuggestionsMatcherVariant.SUBTRUST_LIFETIME_BENEFICIARIES:
      case AISuggestionsMatcherVariant.SUBTRUST_REMAINDER_BENEFICIARIES:
      // Beneficiary-designation recipient matchers reuse the household's
      // possible-beneficiaries set so suggestions can match existing clients /
      // organizations / entities; without these cases they fall through to the
      // empty default and the matcher only offers "Add new".
      case AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_PRIMARY:
      case AISuggestionsMatcherVariant.BENEFICIARY_DESIGNATION_RECIPIENTS_CONTINGENT:
        return {
          clients: possibleBeneficiaries?.clients ?? [],
          organizations: possibleBeneficiaries?.organizations ?? [],
          entities: possibleBeneficiaries?.entities ?? [],
        };
      default:
        // For variants such as BusinessEntity-related people, and DP Recipients,
        // we have other ways to get the options. So just return empty arrays here.
        return { clients: [], organizations: [] };
    }
  }, [
    matcherVariant,
    possibleBeneficiaries,
    possibleGrantors,
    possibleTrustees,
  ]);

  const existingEntities = useMemo(
    () => ('entities' in existingOptions ? existingOptions.entities : []) || [],
    [existingOptions]
  );

  // These options are returned for grantor/trustee/beneficiary/key person/owner
  // suggestions matcher dropdown options.
  const groupedOptions = useMemo(() => {
    if (suggestionKind == AiSuggestionKind.BeneficialOwner) {
      return ownerOptions;
    }

    if (suggestionKind == AiSuggestionKind.KeyPerson) {
      return keyPeopleOptions;
    }

    const individualOptions = sortBy(existingOptions.clients, 'name').map(
      (o) => ({
        value: o.id,
        display: o.name,
        groupName: 'Individuals',
        disabled: matchesFromOtherRows.some((m) => m.clientProfileId === o.id),
      })
    );

    const organizationOptions = sortBy(
      existingOptions.organizations,
      'name'
    ).map((o) => ({
      value: o.id,
      display: o.name,
      groupName: 'Organizations',
      disabled: matchesFromOtherRows.some(
        (m) => m.clientOrganizationId === o.id
      ),
    }));

    const entityOptions = sortBy(existingEntities, 'subtype.displayName').map(
      (e) => ({
        value: e.id,
        display: e.subtype.displayName,
        groupName: 'Entities',
        disabled:
          matchesFromOtherRows.some((m) => m.entityId === e.id) ||
          e.id === entityId,
      })
    );

    // Surviving spouse is only a meaningful option for subtrust beneficiary
    // matchers in two-grantor households (and is not tied to a specific
    // client profile).
    const showSurvivingSpouseOption =
      isTwoClientHousehold &&
      (matcherVariant ===
        AISuggestionsMatcherVariant.SUBTRUST_LIFETIME_BENEFICIARIES ||
        matcherVariant ===
          AISuggestionsMatcherVariant.SUBTRUST_REMAINDER_BENEFICIARIES);

    const survivingSpouseOptions = showSurvivingSpouseOption
      ? [
          {
            value: SURVIVING_SPOUSE_SENTINEL,
            display: 'Surviving spouse',
            disabled: matchesFromOtherRows.some((m) => m.survivingSpouse),
          },
        ]
      : [];

    return [
      ...survivingSpouseOptions,
      ...individualOptions,
      ...organizationOptions,
      ...entityOptions,
    ];
  }, [
    suggestionKind,
    existingOptions.clients,
    existingOptions.organizations,
    existingEntities,
    ownerOptions,
    keyPeopleOptions,
    matchesFromOtherRows,
    entityId,
    isTwoClientHousehold,
    matcherVariant,
  ]);

  const dpRecipientOptionParams = useRecipientOptionsParams();
  const recipientData = useRecipientData()?.data;
  const household = getNodes(recipientData?.households)[0];
  const { typeaheadOptions: dpRecipientOptions } =
    useRecipientOptionsFromHousehold(household, {
      ...dpRecipientOptionParams,
      omitSurvivingSpouseOption: true,
    });

  return {
    options: isDPMatcherVariant(matcherVariant)
      ? dpRecipientOptions
      : groupedOptions,
    household,
    pendingSuggestions: getNodes(recipientData?.aiSuggestions),
  };
}
